// shared/lib/passwordStrength.ts
// Утилита оценки силы пароля — чистая логика без доменных зависимостей.
// Используется в: shared/ui/PasswordStrengthMeter, features/auth/ui/*

import { consola } from 'consola'

const logger = consola.withTag('password-strength')

export enum PasswordStrengthLevel {
  VERY_WEAK = 'VERY_WEAK',
  WEAK = 'WEAK',
  MODERATE = 'MODERATE',
  STRONG = 'STRONG',
  VERY_STRONG = 'VERY_STRONG',
}

export interface PasswordStrengthResult {
  score: number
  level: PasswordStrengthLevel
  suggestions: string[]
  warnings: string[]
  passesMinimum: boolean
  estimatedCrackTime: string
}

export class PasswordStrength {
  private commonPasswords = new Set([
    'password', '123456', '12345678', 'qwerty', 'abc123',
    'monkey', '1234567', 'letmein', 'trustno1', 'dragon',
    'baseball', 'iloveyou', 'master', 'sunshine', 'ashley',
    'bailey', 'passw0rd', 'shadow', '123123', '654321',
    'superman', 'qazwsx', 'michael', 'Football',
  ])

  private commonPatterns = [
    /^123+/,
    /qwerty/i,
    /asdf/i,
    /password/i,
    /admin/i,
    /\b(\w)\1{2,}\b/,
  ]

  constructor() {
    logger.info('Инициализация')
  }

  public evaluate(password: string): PasswordStrengthResult {
    const suggestions: string[] = []
    const warnings: string[] = []
    let score = 0

    if (password.length < 8) {
      warnings.push('Пароль слишком короткий (минимум 8 символов)')
      score = Math.max(0, score - 20)
    } else if (password.length >= 8 && password.length < 12) {
      score += 20
      suggestions.push('Используйте минимум 12 символов для большей безопасности')
    } else if (password.length >= 12 && password.length < 16) {
      score += 30
    } else {
      score += 40
    }

    if (this.isCommonPassword(password)) {
      warnings.push('Это очень популярный пароль, его легко взломать')
      score = Math.max(0, score - 40)
    }

    if (this.hasCommonPattern(password)) {
      warnings.push('Пароль содержит распространенный паттерн')
      score = Math.max(0, score - 20)
    }

    const diversity = this.calculateDiversity(password)
    score += diversity

    if (!this.hasLowerCase(password)) suggestions.push('Добавьте строчные буквы (a-z)')
    if (!this.hasUpperCase(password)) suggestions.push('Добавьте заглавные буквы (A-Z)')
    if (!this.hasNumbers(password)) suggestions.push('Добавьте цифры (0-9)')
    if (!this.hasSpecialChars(password)) suggestions.push('Добавьте специальные символы (!@#$%)')

    if (this.hasSequence(password)) {
      warnings.push('Избегайте последовательностей (123, abc)')
      score = Math.max(0, score - 10)
    }

    if (this.hasRepeats(password)) {
      warnings.push('Избегайте повторяющихся символов (aaa, 111)')
      score = Math.max(0, score - 10)
    }

    score = Math.max(0, Math.min(100, score))
    const level = this.getLevel(score)
    const crackTime = this.estimateCrackTime(password, score)
    const passesMinimum = password.length >= 8 &&
      this.hasLowerCase(password) &&
      this.hasUpperCase(password) &&
      (this.hasNumbers(password) || this.hasSpecialChars(password))

    return { score, level, suggestions, warnings, passesMinimum, estimatedCrackTime: crackTime }
  }

  private isCommonPassword(password: string): boolean {
    return this.commonPasswords.has(password.toLowerCase())
  }

  private hasCommonPattern(password: string): boolean {
    return this.commonPatterns.some(pattern => pattern.test(password))
  }

  private calculateDiversity(password: string): number {
    let score = 0
    if (this.hasLowerCase(password)) score += 5
    if (this.hasUpperCase(password)) score += 5
    if (this.hasNumbers(password)) score += 5
    if (this.hasSpecialChars(password)) score += 10
    if (score === 25) score += 10
    return score
  }

  private hasLowerCase(password: string): boolean { return /[a-z]/.test(password) }
  private hasUpperCase(password: string): boolean { return /[A-Z]/.test(password) }
  private hasNumbers(password: string): boolean { return /\d/.test(password) }
  private hasSpecialChars(password: string): boolean {
    return /[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}-]/.test(password)
  }

  private hasSequence(password: string): boolean {
    const sequences = ['0123', '1234', '2345', '3456', '4567', '5678', '6789',
      'abcd', 'bcde', 'cdef', 'defg', 'efgh', 'fghi', 'ghij']
    return sequences.some(seq => password.toLowerCase().includes(seq))
  }

  private hasRepeats(password: string): boolean { return /(.)\1{2,}/.test(password) }

  private getLevel(score: number): PasswordStrengthLevel {
    /* v8 ignore next */
    if (score >= 80) return PasswordStrengthLevel.VERY_STRONG
    if (score >= 60) return PasswordStrengthLevel.STRONG
    if (score >= 40) return PasswordStrengthLevel.MODERATE
    if (score >= 20) return PasswordStrengthLevel.WEAK
    return PasswordStrengthLevel.VERY_WEAK
  }

  private estimateCrackTime(password: string, _score: number): string {
    const length = password.length
    const diversity = this.calculateDiversity(password) / 35
    const combinations = Math.pow(26 + 26 + 10 + 32, length) * diversity
    const seconds = combinations / 1_000_000_000
    if (seconds < 1) return 'меньше секунды'
    if (seconds < 60) return `${Math.round(seconds)} секунд`
    if (seconds < 3600) return `${Math.round(seconds / 60)} минут`
    if (seconds < 86_400) return `${Math.round(seconds / 3600)} часов`
    if (seconds < 2_592_000) return `${Math.round(seconds / 86_400)} дней`
    if (seconds < 31_536_000) return `${Math.round(seconds / 2_592_000)} месяцев`
    return `${Math.round(seconds / 31_536_000)} лет`
  }

  public getLevelColor(level: PasswordStrengthLevel): string {
    switch (level) {
      case PasswordStrengthLevel.VERY_WEAK: { return '#ef4444'
      }
      case PasswordStrengthLevel.WEAK: { return '#f97316'
      }
      case PasswordStrengthLevel.MODERATE: { return '#eab308'
      }
      case PasswordStrengthLevel.STRONG: { return '#22c55e'
      }
      case PasswordStrengthLevel.VERY_STRONG: { return '#10b981'
      }
      default: { return '#6b7280'
      }
    }
  }

  public getLevelText(level: PasswordStrengthLevel): string {
    switch (level) {
      case PasswordStrengthLevel.VERY_WEAK: { return 'Очень слабый'
      }
      case PasswordStrengthLevel.WEAK: { return 'Слабый'
      }
      case PasswordStrengthLevel.MODERATE: { return 'Средний'
      }
      case PasswordStrengthLevel.STRONG: { return 'Сильный'
      }
      case PasswordStrengthLevel.VERY_STRONG: { return 'Очень сильный'
      }
      default: { return 'Неизвестно'
      }
    }
  }
}

export const passwordStrength = new PasswordStrength()
